/**
 * Tab 6: System Diagnostics (F18)
 * Runs the 5-check diagnostic suite on-demand and shows history.
 *
 * Uses Doyle brand status colors:
 *   pass  -> #28A745 (success green)
 *   warn  -> #F5A623 (amber)
 *   fail  -> #DC3545 (alert red)
 */

import React from 'react';
import {
  runDiagnosticsFull,
  getDiagnosticsHistory,
} from '../../../api/adminApi';
import type {
  DiagnosticReport,
  DiagnosticCheck,
  DiagnosticCheckStatus,
} from '../../../types/admin';

const STATUS_COLOR: Record<DiagnosticCheckStatus, string> = {
  pass: '#28A745',
  warn: '#F5A623',
  fail: '#DC3545',
};

const STATUS_LABEL: Record<DiagnosticCheckStatus, string> = {
  pass: 'PASS',
  warn: 'WARN',
  fail: 'FAIL',
};

function StatusDot({ status }: { status: DiagnosticCheckStatus }) {
  return (
    <span
      aria-label={STATUS_LABEL[status]}
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: STATUS_COLOR[status],
        marginRight: 8,
        verticalAlign: 'middle',
      }}
    />
  );
}

function formatDuration(ms?: number): string {
  if (ms === undefined || ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTimestamp(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function CheckRow({ check }: { check: DiagnosticCheck }) {
  const [open, setOpen] = React.useState(false);
  const hasDetail = Boolean(
    check.detail || check.query || check.actual_top_source || check.score !== undefined,
  );
  return (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid #E9ECEF',
        cursor: hasDetail ? 'pointer' : 'default',
      }}
      onClick={() => hasDetail && setOpen((o) => !o)}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <StatusDot status={check.status} />
          <span style={{ fontWeight: 500 }}>{check.name}</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: '0.85rem', color: '#495057' }}>
          {check.score !== undefined && (
            <span>score: <strong>{check.score.toFixed(3)}</strong></span>
          )}
          {check.duration_ms !== undefined && (
            <span>{formatDuration(check.duration_ms)}</span>
          )}
          <span
            style={{
              padding: '2px 8px',
              borderRadius: 4,
              background: STATUS_COLOR[check.status],
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 600,
              minWidth: 44,
              textAlign: 'center',
            }}
          >
            {STATUS_LABEL[check.status]}
          </span>
        </div>
      </div>
      {open && hasDetail && (
        <div style={{ marginTop: 10, paddingLeft: 18, fontSize: '0.85rem', color: '#495057' }}>
          {check.query && (
            <div><strong>Query:</strong> {check.query}</div>
          )}
          {check.expected_source_contains !== undefined && check.expected_source_contains !== null && (
            <div><strong>Expected source contains:</strong> {check.expected_source_contains}</div>
          )}
          {check.actual_top_source !== undefined && (
            <div><strong>Actual top source:</strong> {check.actual_top_source || '(none)'}</div>
          )}
          {check.min_score !== undefined && (
            <div><strong>Min score:</strong> {check.min_score}</div>
          )}
          {check.detail && (
            <div style={{ marginTop: 6 }}>{check.detail}</div>
          )}
        </div>
      )}
    </div>
  );
}

export const SystemDiagnostics: React.FC = () => {
  const [report, setReport] = React.useState<DiagnosticReport | null>(null);
  const [history, setHistory] = React.useState<DiagnosticReport[]>([]);
  const [running, setRunning] = React.useState(false);
  const [loadingHistory, setLoadingHistory] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedHistory, setExpandedHistory] = React.useState<number | null>(null);

  const loadHistory = React.useCallback(async () => {
    setLoadingHistory(true);
    try {
      const resp = await getDiagnosticsHistory(10);
      setHistory(resp.history || []);
    } catch (e) {
      console.error('[Diagnostics] history load failed:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  React.useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    try {
      const result = await runDiagnosticsFull();
      setReport(result);
      // Auto-refresh history after run completes.
      loadHistory();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to run diagnostics');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="adm-tab-content">
      <div className="adm-tab-header">
        <h2 className="adm-tab-title">System Diagnostics</h2>
      </div>

      {/* Run button */}
      <div className="adm-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 600 }}>Full Diagnostic Run</div>
            <div style={{ fontSize: '0.875rem', color: '#495057', marginTop: 4 }}>
              Executes infrastructure, auth, search quality, end-to-end chat, and
              document-pipeline checks. Takes 20–45 seconds.
            </div>
          </div>
          <button
            className="adm-btn"
            onClick={handleRun}
            disabled={running}
            aria-busy={running}
            style={{ minWidth: 200 }}
          >
            {running ? 'Running…' : 'Run Full Diagnostics'}
          </button>
        </div>
        {error && (
          <div role="alert" style={{ marginTop: 12, padding: 12, background: '#F8D7DA', color: '#721C24', borderRadius: 4, fontSize: '0.875rem' }}>
            {error}
          </div>
        )}
      </div>

      {/* Last run report */}
      {report && (
        <div className="adm-card" style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
              paddingBottom: 12,
              borderBottom: '1px solid #E9ECEF',
            }}
          >
            <div>
              <div style={{ fontSize: '0.875rem', color: '#495057' }}>Last run</div>
              <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                {formatTimestamp(report.timestamp)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <StatusDot status={report.overall_status} />
              <span
                style={{
                  fontWeight: 700,
                  color: STATUS_COLOR[report.overall_status],
                  fontSize: '1.1rem',
                }}
              >
                {STATUS_LABEL[report.overall_status]}
              </span>
              <div style={{ fontSize: '0.85rem', color: '#495057', marginTop: 2 }}>
                {formatDuration(report.duration_ms)} · by {report.triggered_by}
              </div>
            </div>
          </div>
          <div>
            {report.checks.map((check, idx) => (
              <CheckRow key={`${check.name}-${idx}`} check={check} />
            ))}
          </div>
        </div>
      )}

      {/* History — click a row to view that run's individual checks. History
          is stored with full check payloads in Table Storage, so drilling in
          doesn't need a second network call. */}
      <div className="adm-card">
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Recent Runs</div>
        {loadingHistory ? (
          <div style={{ color: '#495057', fontSize: '0.875rem' }}>Loading history…</div>
        ) : history.length === 0 ? (
          <div style={{ color: '#495057', fontSize: '0.875rem' }}>
            No diagnostic runs recorded yet. Click "Run Full Diagnostics" above to create the first one.
          </div>
        ) : (
          <table className="adm-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', width: 24 }}></th>
                <th style={{ textAlign: 'left' }}>Status</th>
                <th style={{ textAlign: 'left' }}>When</th>
                <th style={{ textAlign: 'left' }}>Duration</th>
                <th style={{ textAlign: 'left' }}>Triggered by</th>
                <th style={{ textAlign: 'left' }}>Checks</th>
              </tr>
            </thead>
            <tbody>
              {history.map((run, idx) => {
                const isOpen = expandedHistory === idx;
                return (
                  <React.Fragment key={`${run.timestamp}-${idx}`}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedHistory(isOpen ? null : idx)}
                    >
                      <td style={{ color: '#495057', fontFamily: 'monospace' }}>
                        {isOpen ? '▾' : '▸'}
                      </td>
                      <td>
                        <StatusDot status={run.overall_status} />
                        <span style={{ color: STATUS_COLOR[run.overall_status], fontWeight: 600 }}>
                          {STATUS_LABEL[run.overall_status]}
                        </span>
                      </td>
                      <td>{formatTimestamp(run.timestamp)}</td>
                      <td>{formatDuration(run.duration_ms)}</td>
                      <td>{run.triggered_by}</td>
                      <td>{run.checks?.length ?? 0}</td>
                    </tr>
                    {isOpen && run.checks && run.checks.length > 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0, background: '#F8F9FA' }}>
                          <div style={{ padding: '4px 0' }}>
                            {run.checks.map((check, cidx) => (
                              <CheckRow key={`${run.timestamp}-check-${cidx}`} check={check} />
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
